import { useCallback, useEffect, useState } from 'react'
import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { ChevronLeftIcon } from '@/atoms/Icons/ChevronLeftIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { paths } from '@/constants/paths'
import { EmbeddedStripeCheckoutSession } from '@/molecules/EmbeddedStripeCheckoutSession'
import { GuildUpsellBanner } from '@/organisms/GuildUpsellBanner'
import { RegionalAvailabilityWarning } from '@/organisms/RegionalAvailabilityWarning'
import { getWebClient } from '@/services/ApolloClient'
import { useGuildStripeCheckoutSession } from '@/services/GuildStripeCheckoutSessionService'
import { getGuildStatus } from '@/services/GuildUserService'
import { Offer } from '@/services/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { getStripeCheckoutSessionBilling } from '@/services/StripeService/StripePaymentService'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { getStickyAttribution } from '@/utils/analytics/StickyAttribution'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStringFromLocalStorage, removeFromLocalStorage } from '@/utils/local-storage'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import {
  getObjectFromSessionStorage,
  getStringFromSessionStorage,
  removeFromSessionStorage,
  writeToSessionStorage,
} from '@/utils/session-storage'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildStatus } from '@/utils/users/usersServer'
import { PaypalCheckoutEmbed } from '@/views/GuildStripeCheckoutSessionView/PaypalCheckoutEmbed'

type BillingForTracking = {
  first_name: string
  last_name: string
  address: { city?: string; postal_code?: string; state?: string; country?: string }
}

async function getBillingForTrackingInAnalytics(
  payment_gateway: 'stripe' | 'paypal',
  sessionId?: string | null,
): Promise<BillingForTracking | undefined> {
  if (payment_gateway !== 'stripe' || !sessionId) return undefined
  try {
    const billing = await getStripeCheckoutSessionBilling(sessionId)
    const fullName = (billing?.customerName ?? '').trim()
    const [first, ...rest] = fullName.split(' ')
    const last = rest.join(' ')
    return {
      first_name: first || '',
      last_name: last || '',
      address: {
        city: billing?.billingAddress?.city || '',
        postal_code: billing?.billingAddress?.postal_code || '',
        state: billing?.billingAddress?.state || '',
        country: billing?.billingAddress?.country || '',
      },
    }
  } catch (err: unknown) {
    logger().warn('Failed to fetch Stripe checkout session billing for analytics', { sessionId, err })
    return undefined
  }
}

interface Props {
  email: string
  offer: Offer
  shouldShowAvailabilityWarning: boolean
  isPaypalSupportedPromo: boolean
}

export const GuildEmbeddedStripeCheckoutSession: ReactFCC<Props> = ({
  email,
  offer,
  shouldShowAvailabilityWarning,
  isPaypalSupportedPromo,
}) => {
  const { t } = useTranslate('guild')
  const { query, push, prefetch } = useSafeRouter()
  const track = useSafeTrack()
  const { trackOrderCompleted } = useEcommerceEvents()
  const { locale } = useLocale()
  const client = getWebClient({ locale })
  const [onCompleteLoading, setOnCompleteLoading] = useState(false)
  const [paypalProcessing, setPaypalProcessing] = useState(false)
  const [isCardPaymentSelected, setIsCardPaymentSelected] = useState(false)
  const { campaign, medium, term, content, source, pageSlug, projectSlugs } = getAttribution()

  /** This is Patrick on a weekend removing this, I'd like to retest it in the future **/
  // const checkoutUpsellInitiated = getObjectFromSessionStorage<{ date: string }>('checkoutUpsellInitiated')
  // const wasUpsellInitiatedRecently =
  //   Boolean(checkoutUpsellInitiated) &&
  //   (() => {
  //     const now = Date.now()
  //     const initiated = checkoutUpsellInitiated?.date ? new Date(checkoutUpsellInitiated.date).getTime() : 0
  //     return now - initiated < Durations.ONE_DAY_IN_MILLISECONDS
  //   })()
  // Hide the upsell option if they have clicked it in the past day.
  const canShowUpsell = false //Boolean(!wasUpsellInitiatedRecently && !isPremiumYearly(offer.id))

  // This is necessary because the tool we use to manage redemption codes in Shopify adds variant information to the end of the code and we have no control over how it works,
  // so we need to remove it here for the promo code to properly work with Stripe.
  const cleanShopifyCode = getItemOrFirstEntry(query?.codes)?.split(' ')?.[0] ?? undefined

  const promoCode = getItemOrFirstEntry(query?.promoCode) ?? cleanShopifyCode ?? undefined

  const { loading, clientSecret, currency, amountTotal, sessionId } = useGuildStripeCheckoutSession({
    offerId: offer.id,
    offerName: offer.name,
    email,
    promoCode,
    campaign,
    medium,
    term,
    content,
    source,
    pageSlug,
    projectSlugs,
  })

  const price = getPriceForCurrency(offer.price, offer.currency)
  // Prefetch the success route to make navigation snappier
  useEffect(() => {
    void prefetch(`${paths.guild.checkoutSuccess}?offerId=${offer.id}`)
  }, [prefetch, offer.id])

  const checkAndUpdateUserGuildStatus = useCallback(
    async (
      timeDifference: number,
      oneMinute: number,
      getGuildStatusParam: (client: ApolloClient<NormalizedCacheObject>) => Promise<GuildStatus | null>,
    ): Promise<boolean> => {
      if (timeDifference < oneMinute) {
        if (client) {
          // TODO: I'd modify this, but this one's a little stickier than the others,
          // and to me it looks like there's a preexisting bug. The function is simply
          // called and then true is returned. What this function is supposed to do
          // isn't immediately clear. Maybe there's an "and update" side effect
          // inside the call to getUserGuildStatus, but it doesn't seem like it in the
          // reference passed as the argument arg. Do we need this? Is it a bug?
          // Should it instead return status?.isGuildMember boolean?
          await getGuildStatusParam(client)
        }
        return true
      }
      return false
    },
    [client],
  )

  const onComplete = useCallback(
    async ({ payment_gateway, ...opts }: { payment_gateway: 'stripe' | 'paypal'; ignoreLoading?: boolean }) => {
      const ignoreLoading = Boolean(opts?.ignoreLoading)
      if ((!ignoreLoading && loading) || onCompleteLoading) return

      const checkoutInitiated = getObjectFromSessionStorage<{ date: string }>('checkoutInitiated')
      if (checkoutInitiated) {
        const { date } = checkoutInitiated
        const now = new Date()
        const initiatedDate = new Date(date)
        const timeDifference = now.getTime() - initiatedDate.getTime()
        const oneMinute = 60 * 1000

        if (await checkAndUpdateUserGuildStatus(timeDifference, oneMinute, getGuildStatus)) {
          return
        }
      }

      setOnCompleteLoading(true)

      try {
        const newCheckoutInitiated = {
          date: new Date().toISOString(),
        }
        writeToSessionStorage('checkoutInitiated', newCheckoutInitiated)

        const total = amountTotal ? getPriceForCurrency(amountTotal, currency || 'USD') : 0
        const sharerUuid = getStringFromLocalStorage('sharer_uuid') ?? null

        // Best-effort fetch of Stripe billing details for analytics (Stripe only)
        const billingForTracking = await getBillingForTrackingInAnalytics(payment_gateway, sessionId)

        // Fire analytics synchronously (fire-and-forget) before navigation for higher reliability
        try {
          track(
            'Guild Signup Successful',
            {
              offer: JSON.stringify(offer),
              promoCode,
              email,
              order_id: sessionId, // TODO: i can't see an easy way to get the real order id atm, but kylo said this would be helpful
              checkout_id: sessionId,
              total,
              revenue: price, // without taxes, technically this could be a different currency but not really?
              value: price, // without taxes, technically this could be a different currency but not really?
              currency: currency || 'USD',
              referring_user_id: sharerUuid,
              billing: billingForTracking,
              subscription_metadata: JSON.stringify({
                campaign,
                medium,
                term,
                content,
                source,
                pageSlug,
                projectSlugs,
              }),
              payment_gateway,
            },
            null,
          )

          // Prepare Order Completed payload and include billing for analytics without widening shared types
          const orderCompletedPayload = {
            funnel: 'guild' as const,
            currency: (currency as MoneyCurrencyCode) || 'USD',
            email,
            order_id: sessionId || '',
            total,
            checkout_id: sessionId ?? null,
            coupon: promoCode,
            referring_user_id: sharerUuid,
            subscription: true,
            subtotal: price, // without taxes, technically this could be a different currency but not really?
            projectSlug: projectSlugs ?? null,
            products: [
              {
                category: 'guild',
                name: offer.name,
                price,
                currency: (currency as MoneyCurrencyCode) || 'USD',
                product_id: offer.id,
                quantity: 1,
                sku: offer.id,
              },
            ],
            payment_gateway,
          }

          const orderCompletedPayloadWithNameAndAddress = orderCompletedPayload as typeof orderCompletedPayload & {
            first_name?: string
            last_name?: string
            address?: { city?: string; postal_code?: string; state?: string; country?: string }
          }
          if (billingForTracking) {
            orderCompletedPayloadWithNameAndAddress.first_name = billingForTracking.first_name
            orderCompletedPayloadWithNameAndAddress.last_name = billingForTracking.last_name
            orderCompletedPayloadWithNameAndAddress.address = billingForTracking.address
          }

          trackOrderCompleted(orderCompletedPayloadWithNameAndAddress, null)
        } catch {
          // ignore analytics errors
        }

        // Navigate immediately to reduce re-renders on this page
        const success = await push(`${paths.guild.checkoutSuccess}?offerId=${offer.id}`)

        // Reset Apollo cache after navigation to avoid triggering re-renders on this page
        setTimeout(() => {
          // added the empty `.catch(...)` to handle errors that may happen due to us
          // needing to reset the store after the user has navigated away from this page.
          // Without this, we were seeing uncaught promise rejections in the console
          void client.resetStore().catch(() => {})
        }, 0)

        // If navigation fails, allow retry by unsetting loading
        if (!success) {
          setOnCompleteLoading(false)
        }
      } finally {
        if (getStringFromLocalStorage('sharer_uuid')) {
          removeFromLocalStorage('sharer_uuid')
        }
        // If navigation succeeded, the component will unmount; avoid flicker by not toggling loading back.
      }
    },
    [
      amountTotal,
      client,
      campaign,
      checkAndUpdateUserGuildStatus,
      content,
      currency,
      email,
      loading,
      medium,
      offer,
      onCompleteLoading,
      pageSlug,
      price,
      projectSlugs,
      promoCode,
      push,
      sessionId,
      source,
      term,
      track,
      trackOrderCompleted,
    ],
  )

  const handleBackClick = useCallback(() => {
    if (loading || onCompleteLoading) return

    // If card payment is selected, go back to payment method selection first
    if (isCardPaymentSelected) {
      setIsCardPaymentSelected(false)
      return
    }

    const guildCheckoutOrigin = getStringFromSessionStorage('guildCheckoutOrigin')
    if (guildCheckoutOrigin) {
      removeFromSessionStorage('guildCheckoutOrigin')
      return push(guildCheckoutOrigin)
    } else {
      return push(paths.guild.pricing)
    }
  }, [loading, onCompleteLoading, isCardPaymentSelected, push])

  return (
    <div className="min-h-[560px] w-full max-w-[450px] lg:max-w-[1000px]">
      <div
        onClick={() => {
          if (loading || onCompleteLoading) return
          return track('Guild Checkout Cancelled', { offer: JSON.stringify(offer) })
        }}
        className="mb-4 ml-4 mt-2 lg:ml-[76px] lg:mt-0"
      >
        <InternalLink href={`#`} onClick={handleBackClick} className="flex w-fit items-center text-sm font-bold ">
          <ChevronLeftIcon color="black" className="mr-2" />
          <Translate t={t} i18nKey="goBackAPage">
            Back
          </Translate>
        </InternalLink>
      </div>
      <When condition={canShowUpsell}>
        <GuildUpsellBanner offer={offer} />
      </When>
      <When condition={shouldShowAvailabilityWarning}>
        <RegionalAvailabilityWarning className="rounded-none lg:mx-16 lg:max-w-[875px] lg:rounded-2xl" />
      </When>
      {/* Keep PayPal mounted regardless of promo support to avoid SDK teardown on toggles */}
      <div
        className={isPaypalSupportedPromo && !isCardPaymentSelected ? '' : 'hidden'}
        aria-hidden={!isPaypalSupportedPromo || isCardPaymentSelected}
      >
        <PaypalCheckoutEmbed
          offer={offer}
          promoCode={promoCode}
          onProcessingChange={setPaypalProcessing}
          onApproved={() => onComplete({ payment_gateway: 'paypal', ignoreLoading: true })}
          onCardPaymentSelected={() => setIsCardPaymentSelected(true)}
        />
      </div>
      {(isCardPaymentSelected || !isPaypalSupportedPromo) && (
        <LazyHydrate whenVisible>
          <EmbeddedStripeCheckoutSession
            loading={loading || onCompleteLoading || paypalProcessing}
            clientSecret={clientSecret}
            onComplete={() => onComplete({ payment_gateway: 'stripe' })}
          />
        </LazyHydrate>
      )}
    </div>
  )
}

interface AttributionResult {
  campaign?: string
  source?: string
  content?: string
  medium?: string
  term?: string
  pageSlug?: string | null
  projectSlugs?: string | null
}

export function getAttribution(): AttributionResult {
  const stickyAttributionUtms = getStickyAttribution('guild-attribution')
  const sessionUtms = getObjectFromSessionStorage<SessionUtms>('originalUtms', {})
  const stickyAnalytics = stickyAttributionUtms?.analytics
  let utms = {}

  if (sessionUtms) {
    utms = {
      campaign: sessionUtms.utm_campaign,
      medium: sessionUtms.utm_medium,
      source: sessionUtms.utm_source,
      term: sessionUtms.utm_term,
      content: sessionUtms.utm_content,
    }
  } else if (stickyAnalytics) {
    utms = {
      ...stickyAnalytics,
    }
  }

  return {
    ...utms,
    pageSlug: getStringFromSessionStorage('pageSlug'),
    projectSlugs: getStringFromSessionStorage('projectSlugs'),
  }
}

type SessionUtms = {
  utm_campaign?: string
  utm_source?: string
  utm_medium?: string
  utm_term?: string
  utm_content?: string
}
// Clean file content replaced in a separate step. The full corrected implementation has been re-added.
